import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { loadStripe } from '@stripe/stripe-js/pure.js'
import { forEach } from 'lodash-es'
import * as Sentry from '@sentry/react'
import Announcements from '@components/announcements/Announcements.tsx'
import FlagWebAPI from '@api/flag_web_api.js'
import { addTapListener } from '@utils/event.js'
import SpammableReviewActions from '@components/spam/spammable_review_actions.jsx'
import AdminBarSpammable from '@components/spam/admin_bar_spammable.jsx'
import { loadJSONFromDOMNode } from '@utils/dom_node_data_loader.ts'
import { camelizeKeys } from '@utils/object.js'
import store from '@store/store.ts'
import { components as menuComponents } from '@features/menu/index.js'
import ToasterContainer from '@containers/toaster_container.jsx'
import { Redirector } from '@features/redirection/index.js'
import FormWithCardElements from '@features/stripe/form_with_card_elements.js'
import { containers as searchContainers } from '@features/site_search/index.js'
import { DismissalStore } from '@features/engagement/dismissals'

const { Menu } = menuComponents
const { SearchContainer } = searchContainers

window.Timetoast.shared = {
  redirectWithMessage(url, message, messageType = 'notice') {
    const el = document.getElementById('js-redirector-target')
    if (el) {
      const root = createRoot(el)
      root.render(<Redirector url={url} message={message} type={messageType} wait={3000} />)
    } else {
      console.error('Could not locate redirecter target div')
    }
  },

  siteSearch: {
    init(elementId) {
      const root = createRoot(document.getElementById(elementId))
      root.render(
        <Provider store={store}>
          <SearchContainer />
        </Provider>
      )
    },
  },

  stripe: {
    // Note: This will place Stripe script tag in head, but if it's already there it will just use that instead.
    // This way we get fast access when needed, but it will load Stripe if not on the page already.
    async initStripe(publishableKey) {
      if (!publishableKey) {
        throw new Error('Missing Stripe publishable key')
      }
      const stripe = await loadStripe(publishableKey)
      return stripe
    },

    async doHandleCardPayment(options = {}) {
      const {
        publishableKey,
        paymentIntentClientSecret,
        routes: { successURL, failURL },
      } = options
      const stripe = await window.Timetoast.shared.stripe.initStripe(publishableKey)
      options = {
        ...options,
        stripe: stripe,
      }

      const paymentErrorExplanation = document.querySelector('.js-payment-error-explanation')
      if (paymentErrorExplanation !== null) {
        paymentErrorExplanation.classList.add('hidden')
      }

      stripe.handleCardPayment(paymentIntentClientSecret).then(
        res => {
          const { error, paymentIntent } = res
          if (error) {
            const { message } = error
            window.Timetoast.shared.redirectWithMessage(failURL, message, 'alert')
            Sentry.captureMessage(`Stripe handleCardPayment auth failure. Message: ${message}`)
          } else if (paymentIntent) {
            if (paymentIntent.status === 'succeeded') {
              window.Timetoast.shared.redirectWithMessage(
                successURL,
                'Card payment successful. Your subscription will be (re)activated soon, redirecting...',
                'success'
              )
            } else {
              window.Timetoast.shared.redirectWithMessage(
                failURL,
                'Card payment failed. Code DHCP-1a error. Redirecting...',
                'alert'
              ) // somehow no payment intent
              Sentry.captureMessage(
                'Stripe handleCardPayment call did not return a paymentIntent in the res object. Code DHCP-1a.'
              )
            }
          }
        },
        err => {
          window.Timetoast.shared.redirectWithMessage(
            failURL,
            'Card payment failed. Code DHCP-1b error occured handling card payment. Redirecting...',
            'alert'
          ) // probably server error at stripe or something
          Sentry.captureException(err)
        }
      )
    },

    setupForm: async function (options = {}) {
      const { publishableKey } = options
      const stripe = await window.Timetoast.shared.stripe.initStripe(publishableKey)
      options = {
        ...options,
        stripe,
      }
      const formWithCardElements = new FormWithCardElements(options)
      formWithCardElements.init()
    },
  },

  spam: {
    // finds all spam reporting links and attaches a listener to their clicks
    initSpamReporting: function () {
      const elements = document.querySelectorAll('.js-report-spam-link')
      forEach(elements, function (element) {
        const clickHandler = event => {
          event.preventDefault()
          element.text = 'Reporting...'
          // TODO: Replace links with regular span element?
          // Note: Always respond as if successful
          const responseHandler = resOrErr => {
            element.text = 'Reported, thanks!'
          }
          FlagWebAPI.createFlag(element.dataset.model, element.dataset.id).then(responseHandler, responseHandler)
        }
        addTapListener(element, clickHandler)
      })
    },

    renderSpamHamReviewActions: function (targetClassName = 'js-spammable-review-actions') {
      const targetEls = document.getElementsByClassName(targetClassName)
      forEach(targetEls, targetEl => {
        const { spammableId, spammableType, className } = targetEl.dataset
        const root = createRoot(targetEl)
        root.render(<SpammableReviewActions spammableId={spammableId} spammableType={spammableType} />)
      })
    },

    renderAdminBarSpammable: function (targetID, options) {
      const { ...rest } = options
      const root = createRoot(document.getElementById(targetID))
      root.render(<AdminBarSpammable {...rest} />)
    },
  },

  announcements: {
    init: function (targetID, options) {
      const root = createRoot(document.getElementById(targetID))
      root.render(<Announcements options={options} />, document.getElementById(targetID))
    },
  },

  navigations: {
    init: function () {
      const menus = document.querySelectorAll('.js-menu-takeover')
      forEach(menus, targetEl => {
        const {
          dataset: { dataDomId },
        } = targetEl
        const json = loadJSONFromDOMNode(dataDomId)
        const menuData = camelizeKeys(json)
        const { ...rest } = menuData
        const root = createRoot(targetEl)
        root.render(<Menu {...rest} />)
      })
    },
  },

  toaster: {
    init: function (targetID = 'toaster-react-target') {
      const root = createRoot(document.getElementById(targetID))
      root.render(
        <Provider store={store}>
          <div>
            <ToasterContainer />
          </div>
        </Provider>
      )
    },
  },

  initAdLabels: function () {
    const loopFn = function () {
      const reclames = document.getElementsByClassName('reclame')
      forEach(reclames, reclame => {
        if (reclame.classList.contains('reclame--in-content')) {
          // don't do in-content ads for now, we'll do that later
          return
        }
        const adNodes = reclame.getElementsByClassName('ad')
        if (adNodes.length > 0) {
          const adNode = adNodes[0]
          const adElementChild = adNode.firstElementChild
          if (adElementChild && adElementChild.clientWidth > 0 && adElementChild.clientHeight > 0) {
            reclame.classList.add('reclame--filled')
          }
        }
      })
    }
    loopFn()
    setInterval(loopFn, 1000)
  },
}

// Expose engagement dismissal store for non-module scripts (ERB inline scripts, etc.)
window.Timetoast.engagement = {
  ...(window.Timetoast.engagement || {}),
  dismissals: {
    ...(window.Timetoast.engagement?.dismissals || {}),
    DismissalStore,
  },
}
